/**
 * Country-aware formatters. Currency symbol, decimal separator, digit
 * grouping, and compact suffix all derive from the active country.
 *
 * Never write `₹` / `$` / `£` in component code — call these.
 */
import { activeCountry, type Country } from "./country";

/** Full money rendering, e.g. ₹50,000 (IN) · $50,000 (US). */
export function formatMoney(amount: number, country: Country = activeCountry()): string {
  const fmt = new Intl.NumberFormat(country.locale, {
    style: "currency",
    currency: country.currency,
    maximumFractionDigits: 0,
  });
  return fmt.format(amount);
}

/** Country-aware compact form. INR uses Lakh / Crore (1,00,000-grouped),
 *  USD/EUR/GBP/AED/SGD use the metric Million/Billion ladder. */
export function formatCompactMoney(amount: number, country: Country = activeCountry()): string {
  const sym = country.currencySymbol;
  if (amount < 0) return `-${formatCompactMoney(-amount, country)}`;
  for (const { n, suffix } of country.compactSuffixes) {
    if (amount >= n) {
      const value = amount / n;
      const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
      return `${sym}${value.toFixed(digits)} ${suffix}`;
    }
  }
  // Below the smallest compact threshold — render with locale grouping.
  return `${sym}${amount.toLocaleString(country.locale)}`;
}

/** Date in the country's preferred format. */
export function formatDate(d: Date | string, country: Country = activeCountry()): string {
  const date = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat(country.locale).format(date);
}
